import { getTourStatus, getTourTournamentIds, Tour } from "model/Tour/Tour";
import {
  getSegmentFilterName,
  getSegmentFromFilterName,
  getSegmentMatchIds,
  segmentContainsMatch,
  TournamentSegment,
} from "model/Tournament/Segment/TournamentSegment";
import {
  getTournamentMatchIds,
  Tournament,
  TournamentStatus,
} from "model/Tournament/Tournament";
import { RootState } from "app/store";
import { nonUndefined, onlyUnique } from "Utility";
import { selectTournamentIdsWithPickem } from "./PickemDetails";
import {
  selectNonTourTournaments,
  selectTournamentStatus,
  selectToursByYear,
  selectTourTitleDisplayForTournamentId,
} from "./Tour";
import { selectTournamentIdsForTrackFilters } from "./Track";
import {
  selectMatches,
  selectMatchesRosterIds,
  selectTournamentIdFromMatchId,
} from "./Match";
import { calculateTeamRecord } from "model/Match/Matches";
import {
  getTournamentDriverIds,
  getTournamentRosterIdFromDriverId,
} from "model/Tournament/TournamentTeams";

export const selectAllTournamentIds = (state: RootState) => {
  return Object.keys(state.tournament.tournaments);
};

export const selectAllTournaments = (state: RootState) => {
  return nonUndefined(Object.values(state.tournament.tournaments));
};

export const selectTournament = (
  state: RootState,
  tournamentId: string | undefined
) => {
  if (!tournamentId) {
    return undefined;
  }

  return state.tournament.tournaments[tournamentId];
};

export const selectTournaments = (
  state: RootState,
  tournamentIds: string[]
) => {
  return nonUndefined(tournamentIds.map((id) => selectTournament(state, id)));
};

export const selectTournamentName = (
  state: RootState,
  tournamentId: string | undefined
) => {
  return selectTournament(state, tournamentId)?.name;
};

export const selectTournamentTrigram = (
  state: RootState,
  tournamentId: string | undefined
) => {
  return selectTournament(state, tournamentId)?.trigram;
};

export const selectTournamentTeams = (
  state: RootState,
  tournamentId: string
) => {
  return selectTournament(state, tournamentId)?.teams;
};

export const selectTournamentRosterScope = (
  state: RootState,
  tournamentId: string
) => {
  return selectTournamentTeams(state, tournamentId)?.scope;
};

export const selectDriverRosterIdFromTournament = (
  state: RootState,
  tournamentId: string,
  driverId: string
) => {
  const teams = selectTournamentTeams(state, tournamentId);
  if (!teams) {
    return undefined;
  }

  return getTournamentRosterIdFromDriverId(teams, driverId);
};

export const selectTournamentsDriverIds = (
  state: RootState,
  tournamentIds: string[]
) => {
  const driverIds = tournamentIds.flatMap((id) => {
    const teams = selectTournamentTeams(state, id);
    if (!teams) {
      return [];
    }

    return getTournamentDriverIds(teams);
  });

  return driverIds.filter(onlyUnique);
};

export const selectTournamentTrackIds = (
  state: RootState,
  tournamentId: string | undefined
) => {
  return selectTournament(state, tournamentId)?.tracks.map((t) => t.id);
};

export const selectTournamentTracks = (
  state: RootState,
  tournamentId: string
) => {
  return selectTournament(state, tournamentId)?.tracks;
};

export const selectTournamentHasTracks = (
  state: RootState,
  tournamentId: string
) => {
  const tracks = selectTournamentTracks(state, tournamentId);
  return !!tracks && tracks.length > 0;
};

export const selectTournamentMatchIds = (
  state: RootState,
  tournamentId: string | undefined
) => {
  const tournament = selectTournament(state, tournamentId);
  return !!tournament ? getTournamentMatchIds(tournament) : undefined;
};

export const selectTournamentSegment = (
  state: RootState,
  tournamentId: string | undefined,
  segmentId: string | undefined
) => {
  if (!tournamentId || !segmentId) {
    return undefined;
  }

  const tournament = selectTournament(state, tournamentId);
  if (!tournament) {
    return undefined;
  }

  return tournament.segments.find((s) => s.segmentId === segmentId);
};

export const selectTournamentSegments = (
  state: RootState,
  tournamentId: string,
  segmentIds?: string[]
) =>
  selectTournament(state, tournamentId)?.segments.filter(
    (s) => !segmentIds || segmentIds.includes(s.segmentId)
  );

export const selectTournamentSegmentNames = (
  state: RootState,
  tournamentId: string
) => selectTournament(state, tournamentId)?.segments.map((s) => s.name);

export const selectTournamentSegmentFilterOptions = (
  state: RootState,
  tournamentId: string
) => {
  const tournament = selectTournament(state, tournamentId);
  return tournament?.segments
    .map((s) => getSegmentFilterName(s, tournament.segmentLinks))
    ?.filter(onlyUnique);
};

export const selectTournamentSegmentRosterIds = (
  state: RootState,
  segment: TournamentSegment
) => {
  const matchIds = getSegmentMatchIds(segment);
  return selectMatchesRosterIds(state, matchIds);
};

export const selectTournamentsByYear = (state: RootState, year: string) => {
  const yearNum = parseInt(year);
  const allTournaments = nonUndefined(selectAllTournaments(state));
  if (!yearNum || isNaN(yearNum)) {
    return allTournaments;
  }

  return allTournaments.filter((t) => {
    const startDate = new Date(t.startTimeMs);
    const endDate = new Date(t.endTimeMs);
    return (
      yearNum >= startDate.getFullYear() && yearNum <= endDate.getFullYear()
    );
  });
};

export const selectTournamentsByFilter = (
  state: RootState,
  year: string | undefined,
  stageName: string | undefined,
  tournamentFilters: string[]
) => {
  const tours = selectToursByYear(state, year);
  const stages = !stageName
    ? tours.flatMap((t) => t.stages)
    : tours.flatMap((t) => t.stages).filter((s) => s.name === stageName);
  const tournamentIds = stages.flatMap((s) => s.tournamentIds);
  const tournaments: Tournament[] = [];
  tournamentIds.forEach((id) => {
    const tournament = selectTournament(state, id);
    if (!!tournament && tournamentFilters.includes(tournament.filterName)) {
      tournaments.push(tournament);
    }
  });

  return tournaments;
};

export const selectTournamentEndTime = (
  state: RootState,
  tournamentId: string
) => {
  const tournament = selectTournament(state, tournamentId);
  if (!tournament) {
    return undefined;
  }

  return tournament.endTimeMs;
};

export const selectTournamentImageId = (
  state: RootState,
  tournamentId: string
) => {
  return selectTournament(state, tournamentId)?.imageIdOverride ?? tournamentId;
};

export const selectTournamentSegmentTitle = (
  state: RootState,
  segmentName: string,
  tournamentId: string
) => {
  const tournament = selectTournament(state, tournamentId);
  if (!tournament) {
    return undefined;
  }

  const tournamentTitle = `${tournament.name} ${segmentName}`;
  const tourTitle = selectTourTitleDisplayForTournamentId(state, tournamentId);
  return !!tourTitle ? `${tourTitle} ${tournamentTitle}` : tournamentTitle;
};

export const selectTournamentIdFromName = (
  state: RootState,
  tournamentName: string
) => {
  return selectAllTournamentIds(state).find(
    (id) => selectTournamentName(state, id) === tournamentName
  );
};

// todo - refactor, not very readable, perhaps redo whole filter system?
export const selectTournamentIdsFromFilters = (
  state: RootState,
  year: string,
  tourOrTournamentName: string | undefined,
  subTourNames: string[] | undefined,
  tournamentFilters: string[] | undefined,
  includeNonTourTournaments: boolean = false
) => {
  const tours = selectToursByYear(state, year);
  const nonTourTournaments = selectNonTourTournaments(state, year);
  var tournamentIds: string[];
  if (!!tourOrTournamentName) {
    const tour = tours.find((t) => t.name === tourOrTournamentName);
    if (!!tour) {
      if (!subTourNames) {
        tournamentIds = getTourTournamentIds(tour);
      } else {
        tournamentIds = tour.stages
          .filter((s) => !!s.name && subTourNames.includes(s.name))
          .flatMap((s) => s.tournamentIds);
      }
    } else {
      const tournament = nonTourTournaments.find(
        (t) => t.name === tourOrTournamentName
      );
      tournamentIds = !!tournament ? [tournament.id] : [];
    }
  } else {
    const tourTournamentIds = tours.flatMap(getTourTournamentIds);
    const nonTourTournamentIds = includeNonTourTournaments
      ? nonTourTournaments.flatMap((t) => t.id)
      : [];
    tournamentIds = tourTournamentIds.concat(nonTourTournamentIds);
  }

  if (!!tournamentFilters) {
    tournamentIds = tournamentIds.filter((id) => {
      const filterName = selectTournament(state, id)?.filterName;
      if (!filterName) {
        return false;
      }

      return tournamentFilters.includes(filterName);
    });
  }
  return tournamentIds.filter(onlyUnique);
};

export const selectTournamentFilterOptionsFromFilters = (
  state: RootState,
  year: string,
  tourOrTournamentName: string | undefined,
  subTourNames: string[] | undefined,
  includeNonTourTournaments: boolean = false
) => {
  const tournamentIds = selectTournamentIdsFromFilters(
    state,
    year,
    tourOrTournamentName,
    subTourNames,
    undefined,
    includeNonTourTournaments
  );

  return nonUndefined(
    tournamentIds.map((id) => selectTournament(state, id)?.filterName)
  ).filter(onlyUnique);
};

export const selectTournamentPickemFilterOptions = (
  state: RootState,
  year: string
) => {
  const pickemTournamentIds = selectTournamentIdsWithPickem(state, year);
  return nonUndefined(
    pickemTournamentIds.map((id) => selectTournamentName(state, id))
  ).filter(onlyUnique);
};

export const selectTournamentFilterOptionsFromTrackId = (
  state: RootState,
  trackId: string | undefined
) => {
  if (!trackId) {
    return [];
  }

  const tournamentIds = selectTournamentIdsForTrackFilters(state, trackId);

  return nonUndefined(
    tournamentIds.map((id) => selectTournament(state, id)?.filterName)
  ).filter(onlyUnique);
};

// todo - deal with offset of endTime?
// todo - deal with tours better?
// todo - improve all in all
export const selectTournamentsAndToursByStatus = (
  state: RootState,
  tournaments: Tournament[],
  tours: Tour[],
  status: TournamentStatus,
  sort: "None" | "Ascending" | "Descending"
) => {
  const tournamentsMatchingStatus: { type: "Tournament"; data: Tournament }[] =
    tournaments
      .filter((t) => selectTournamentStatus(state, t) === status)
      .map((t) => ({ type: "Tournament", data: t }));

  const toursMatchingStatus: { type: "Tour"; data: Tour }[] = tours
    .filter((t) => getTourStatus(t) === status)
    .map((t) => ({ type: "Tour", data: t }));
  const all = [...tournamentsMatchingStatus, ...toursMatchingStatus];
  if (sort === "Ascending" || sort === "Descending") {
    return sort === "Ascending"
      ? all.sort((a, b) => a.data.startTimeMs - b.data.startTimeMs)
      : all.sort((a, b) => b.data.endTimeMs - a.data.endTimeMs);
  }

  return all;
};

export const selectRosterSegmentRecordFromMatchId = (
  state: RootState,
  matchId: string,
  rosterId: string
) => {
  const tournamentId = selectTournamentIdFromMatchId(state, matchId);
  const tournament = !!tournamentId
    ? selectTournament(state, tournamentId)
    : undefined;
  if (!tournament) {
    return undefined;
  }

  const segment = tournament.segments.find((s) =>
    segmentContainsMatch(s, matchId)
  );

  if (!segment) {
    return undefined;
  }

  if (segment.type === "Season" || segment.type === "Swiss") {
    const group = segment.groups.find((g) =>
      g.rounds.flatMap((r) => r.matchIds).includes(matchId)
    );

    if (!group) {
      return undefined;
    }

    const recordMatchIds: string[] = [matchId];
    for (let p = 0; p < group.rounds.length; p++) {
      const round = group.rounds[p];
      if (round.matchIds.includes(matchId)) {
        break;
      }

      recordMatchIds.push(...round.matchIds);
    }

    return calculateTeamRecord(selectMatches(state, recordMatchIds), rosterId);
  }

  return undefined;
};

export const selectTrackNameFromTournamentId = (
  state: RootState,
  trackId: string,
  tournamentId: string
) => {
  return selectTournament(state, tournamentId)?.tracks.find(
    (t) => t.id === trackId
  )?.name;
};

export const selectTournamentSegmentsFromFilters = (
  state: RootState,
  tournamentId: string,
  segmentFilters: string[]
) => {
  const tournament = selectTournament(state, tournamentId);
  if (!tournament) {
    return undefined;
  }

  return nonUndefined(
    segmentFilters.map((f) =>
      getSegmentFromFilterName(f, tournament.segments, tournament.segmentLinks)
    )
  );
};
